/**
 * Control Plane API Client
 * Handles JWT-based authentication with control.zaviago.com
 */

export const CONTROL_PLANE_URL =
  import.meta.env.VITE_CONTROL_PLANE_URL || 'https://control.zaviago.com';

// JWT token storage keys
export const CP_TOKEN_KEY = 'control_plane_jwt';
export const CP_REFRESH_KEY = 'control_plane_refresh';
export const CP_EXPIRY_KEY = 'control_plane_jwt_expiry';

export const CP_SERVER_URL_KEY = 'control_plane_server_url';
export const CP_PROVISIONED_KEY = 'control_plane_is_provisioned';
export const CP_STOREFRONT_URL_KEY = 'control_plane_storefront_url';

export const getCPToken = (): string | null => localStorage.getItem(CP_TOKEN_KEY);
export const getCPRefreshToken = (): string | null => localStorage.getItem(CP_REFRESH_KEY);
export const getCPTokenExpiry = (): string | null => localStorage.getItem(CP_EXPIRY_KEY);
export const getCPServerUrl = (): string | null => localStorage.getItem(CP_SERVER_URL_KEY);
export const getCPIsProvisioned = (): boolean => localStorage.getItem(CP_PROVISIONED_KEY) === '1';
export const getCPStorefrontUrl = (): string | null => localStorage.getItem(CP_STOREFRONT_URL_KEY);

export const setCPToken = (token: string, expiresAt: string, refreshToken?: string): void => {
  localStorage.setItem(CP_TOKEN_KEY, token);
  localStorage.setItem(CP_EXPIRY_KEY, expiresAt);
  if (refreshToken) {
    localStorage.setItem(CP_REFRESH_KEY, refreshToken);
  }
};

export const setCPTenantMeta = (serverUrl: string, isProvisioned: boolean, storefrontUrl: string | null): void => {
  localStorage.setItem(CP_SERVER_URL_KEY, serverUrl);
  localStorage.setItem(CP_PROVISIONED_KEY, isProvisioned ? '1' : '0');
  if (storefrontUrl) localStorage.setItem(CP_STOREFRONT_URL_KEY, storefrontUrl);
  else localStorage.removeItem(CP_STOREFRONT_URL_KEY);
};

export const clearCPToken = (): void => {
  localStorage.removeItem(CP_TOKEN_KEY);
  localStorage.removeItem(CP_REFRESH_KEY);
  localStorage.removeItem(CP_EXPIRY_KEY);
  localStorage.removeItem(CP_SERVER_URL_KEY);
  localStorage.removeItem(CP_PROVISIONED_KEY);
  localStorage.removeItem(CP_STOREFRONT_URL_KEY);
};

// Cross-tab refresh mutex (localStorage-based)
const CP_REFRESH_LOCK_KEY = 'control_plane_refresh_lock';
const REFRESH_LOCK_TTL_MS = 10_000; // 10 seconds

// Same-tab deduplication: cache the in-flight refresh Promise
let inflightRefresh: Promise<{ access_token: string; expires_at: string } | null> | null = null;

// Callback to sync refreshed tokens into Zustand store.
// Set by authStore.ts at initialization to avoid circular import.
let _onTokenRefresh: ((token: string, expiresAt: string) => void) | null = null;
export function setTokenRefreshCallback(cb: (token: string, expiresAt: string) => void): void {
  _onTokenRefresh = cb;
}

function acquireRefreshLock(): boolean {
  const existing = localStorage.getItem(CP_REFRESH_LOCK_KEY);
  if (existing) {
    try {
      const { ts } = JSON.parse(existing);
      if (Date.now() - ts < REFRESH_LOCK_TTL_MS) return false; // Lock is held and not expired
    } catch { /* corrupted lock — treat as expired */ }
  }
  localStorage.setItem(CP_REFRESH_LOCK_KEY, JSON.stringify({ ts: Date.now() }));
  return true;
}

function releaseRefreshLock(): void {
  localStorage.removeItem(CP_REFRESH_LOCK_KEY);
}

/**
 * Decode JWT expiry from access token payload.
 * Returns ISO string of expiry date, or null if token can't be decoded.
 * Does NOT validate signature — that's the server's job.
 */
export function decodeJwtExpiry(token: string): string | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    // base64url → base64 normalization (consistent with decodeJwtPayload)
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    if (typeof payload.exp === 'number') {
      return new Date(payload.exp * 1000).toISOString();
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Decode the full JWT payload (claims) from an access token.
 * Returns the parsed payload object, or null if the token can't be decoded.
 * Handles base64url encoding (RFC 7515) correctly.
 * Does NOT validate signature — that's the server's job.
 */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    // JWT uses base64url encoding — normalize to standard base64 for atob()
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

/**
 * Refresh the access token using the stored refresh token.
 * Returns new tokens on success, null on failure.
 * Automatically updates localStorage with new tokens.
 *
 * Cross-tab: Uses a localStorage mutex so only one tab makes the refresh
 * network call. Losing tabs wait with jitter, then read the fresh token.
 * Same-tab: Deduplicates via inflightRefresh Promise cache.
 */
export async function refreshCPToken(): Promise<{ access_token: string; expires_at: string } | null> {
  // Same-tab deduplication: if a refresh is already in-flight, share its Promise
  if (inflightRefresh) return inflightRefresh;

  const refreshToken = getCPRefreshToken();
  if (!refreshToken) return null;

  inflightRefresh = (async () => {
    let didAcquire = false;

    // Cross-tab mutex: if another tab is already refreshing, wait and read the result
    if (!acquireRefreshLock()) {
      const jitter = 500 + Math.random() * 1000; // 500-1500ms
      await new Promise(r => setTimeout(r, jitter));
      // Read the token the winning tab wrote
      const token = getCPToken();
      const expiry = getCPTokenExpiry();
      if (token && expiry) return { access_token: token, expires_at: expiry };
      // Winning tab failed — re-acquire lock and try ourselves
      acquireRefreshLock(); // best-effort: if another tab grabs it, we still proceed (benign)
      didAcquire = true;
    } else {
      didAcquire = true;
    }

    try {
      const response = await fetch(`${CONTROL_PLANE_URL}/api/method/control_plane.api.auth.refresh_tokens`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'omit',
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) return null;

      const data = await response.json();
      const result = data.message;

      if (!result?.access_token) return null;

      const expiresAt = decodeJwtExpiry(result.access_token)
        || new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();

      setCPToken(result.access_token, expiresAt, result.refresh_token || refreshToken);

      return { access_token: result.access_token, expires_at: expiresAt };
    } catch {
      return null;
    } finally {
      if (didAcquire) releaseRefreshLock();
    }
  })();

  try {
    return await inflightRefresh;
  } finally {
    inflightRefresh = null;
  }
}

// Types matching actual Control Plane API response
export interface CPLoginResponse {
  success: boolean;
  tenant_id: string;
  tenant_slug: string;
  user_id: string;
  access_token: string;
  refresh_token: string;
  server_url: string;
  company: string;
  is_provisioned: boolean;
  storefront_url: string;
}

export interface CPLogoutResponse {
  success: boolean;
  message: string;
}

// API call wrapper for authenticated endpoints
export async function cpApiCall<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getCPToken();

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  const response = await fetch(`${CONTROL_PLANE_URL}/api/method/${endpoint}`, {
    ...options,
    headers,
    credentials: 'omit', // Don't send cookies - we use JWT Bearer tokens
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));

    // Frappe wraps errors in _server_messages (JSON string array)
    let message = 'API Error';
    if (error._server_messages) {
      try {
        const msgs = JSON.parse(error._server_messages);
        // msgs[0] is itself a JSON-encoded string: '{"message": "...", "indicator": "red", ...}'
        let parsed = msgs[0];
        if (typeof parsed === 'string') {
          try { parsed = JSON.parse(parsed); } catch { /* use as-is */ }
        }
        message = typeof parsed === 'object' && parsed?.message
          ? String(parsed.message)
          : String(parsed);
      } catch {
        message = String(error._server_messages);
      }
    } else if (error.message) {
      message = error.message;
    } else if (error.exc_type) {
      message = `${error.exc_type}: ${error.exc || 'Unknown error'}`;
    }

    throw new Error(message);
  }

  const data = await response.json();
  // Frappe wraps response in { message: {...} }
  return data.message as T;
}

// Guest API call (no Authorization header) - for login/register and LINE OA proxied endpoints
export async function cpGuestApiCall<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  let response = await fetch(`${CONTROL_PLANE_URL}/api/method/${endpoint}`, {
    ...options,
    headers,
    credentials: 'omit', // Don't send cookies
  });

  // 401 retry: if body contained access_token, refresh and retry once
  if (response.status === 401 && options.body) {
    let bodyObj: Record<string, unknown> | null = null;
    try { bodyObj = JSON.parse(options.body as string); } catch { /* not JSON */ }

    if (bodyObj?.access_token) {
      const refreshResult = await refreshCPToken();
      if (refreshResult) {
        _onTokenRefresh?.(refreshResult.access_token, refreshResult.expires_at);
        bodyObj.access_token = refreshResult.access_token;
        response = await fetch(`${CONTROL_PLANE_URL}/api/method/${endpoint}`, {
          ...options,
          headers,
          credentials: 'omit',
          body: JSON.stringify(bodyObj),
        });
      }
    }
  }

  if (!response.ok) {
    // If still 401 after refresh attempt, clear stale tokens so auth guards redirect to login
    if (response.status === 401) {
      clearCPToken();
    }
    const error = await response.json().catch(() => ({}));

    let message = 'API Error';
    if (error._server_messages) {
      try {
        const msgs = JSON.parse(error._server_messages);
        // msgs[0] is itself a JSON-encoded string: '{"message": "...", "indicator": "red", ...}'
        let parsed = msgs[0];
        if (typeof parsed === 'string') {
          try { parsed = JSON.parse(parsed); } catch { /* use as-is */ }
        }
        message = typeof parsed === 'object' && parsed?.message
          ? String(parsed.message)
          : String(parsed);
      } catch {
        message = String(error._server_messages);
      }
    } else if (error.message) {
      message = error.message;
    } else if (error.exc_type) {
      message = `${error.exc_type}: ${error.exc || 'Unknown error'}`;
    }

    throw new Error(message);
  }

  const data = await response.json();
  return data.message as T;
}

// Convenience methods
export async function cpLogin(email: string, password: string): Promise<CPLoginResponse> {
  // Clear any stale tokens before login attempt
  clearCPToken();

  return cpGuestApiCall<CPLoginResponse>('control_plane.api.auth.login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
}

export async function cpLogout(): Promise<CPLogoutResponse> {
  return cpApiCall<CPLogoutResponse>('control_plane.api.auth.logout', {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

// === Registration Types ===

export interface BusinessType {
  name: string;
  business_type_name: string;
  icon: string;
}

export interface AvailableApp {
  name: string;
  app_name: string;
  description: string;
  icon: string;
  // Rich marketplace fields (Epic 01 API)
  logo_url?: string;
  category?: string;
  pricing_type?: "Free" | "Paid" | "Freemium";
  monthly_price_thb?: number;
  average_rating?: number;
  review_count?: number;
  sort_order?: number;
}

export interface RegisterPayload {
  email: string;
  password: string;
  tenant_name: string;
  first_name?: string;
  last_name?: string;
  phone_number?: string;
  business_type?: string;
  app_interests?: string[];
  intent?: string;  // LP intent (e.g. 'selling', 'chat', 'contacts')
  qualification_data?: string;  // Intent-specific qualification answers (JSON string)
  real_email?: string;  // Real email from desktop handoff (for Google Ads ECL)
}

export interface RegisterResponse {
  success: boolean;
  tenant_id: string;
  user_id: string;
  access_token: string;
  refresh_token: string;
  server_url: string;
  company: string;
  is_provisioned: boolean;
  storefront_url: string;
}

// === Registration API Functions ===

export async function getBusinessTypes(): Promise<BusinessType[]> {
  const response = await fetch(`${CONTROL_PLANE_URL}/api/method/control_plane.api.master.get_business_types`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'omit',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch business types');
  }

  const data = await response.json();
  return data.message || [];
}

export async function getAvailableApps(): Promise<AvailableApp[]> {
  const response = await fetch(`${CONTROL_PLANE_URL}/api/method/control_plane.api.master.get_available_apps`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'omit',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch available apps');
  }

  const data = await response.json();
  return data.message || [];
}

export async function getMarketplaceApps(): Promise<AvailableApp[]> {
  const response = await fetch(`${CONTROL_PLANE_URL}/api/method/control_plane.api.master.get_marketplace_apps`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'omit',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch marketplace apps');
  }

  const data = await response.json();
  return data.message || [];
}

export async function getMarketplaceAppDetail(appName: string) {
  const response = await fetch(
    `${CONTROL_PLANE_URL}/api/method/control_plane.api.master.get_marketplace_app_detail?app_name=${encodeURIComponent(appName)}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'omit',
    }
  );

  if (!response.ok) {
    throw new Error('App not found');
  }

  const data = await response.json();
  return data.message;
}

// === App Install/Remove API Functions ===

export async function cpInstallApp(appName: string): Promise<{ success: boolean; message: string }> {
  const token = getCPToken();
  return cpGuestApiCall<{ success: boolean; message: string }>(
    'control_plane.api.apps.enable_app',
    {
      method: 'POST',
      body: JSON.stringify({ access_token: token, app_name: appName }),
    }
  );
}

export async function cpRemoveApp(appName: string): Promise<{ success: boolean; message: string }> {
  const token = getCPToken();
  return cpGuestApiCall<{ success: boolean; message: string }>(
    'control_plane.api.apps.disable_app',
    {
      method: 'POST',
      body: JSON.stringify({ access_token: token, app_name: appName }),
    }
  );
}

export async function cpRegister(payload: RegisterPayload): Promise<RegisterResponse> {
  const response = await fetch(`${CONTROL_PLANE_URL}/api/method/control_plane.api.auth.register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'omit',
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));

    let message = 'Registration failed';
    if (error._server_messages) {
      try {
        const msgs = JSON.parse(error._server_messages);
        // msgs[0] is itself a JSON-encoded string: '{"message": "...", "indicator": "red", ...}'
        let parsed = msgs[0];
        if (typeof parsed === 'string') {
          try { parsed = JSON.parse(parsed); } catch { /* use as-is */ }
        }
        message = typeof parsed === 'object' && parsed?.message
          ? String(parsed.message)
          : String(parsed);
      } catch {
        message = String(error._server_messages);
      }
    } else if (error.message) {
      message = error.message;
    }

    throw new Error(message);
  }

  const data = await response.json();
  return data.message;
}

// === Google One Tap ===

export interface GoogleOneTapPayload {
  credential: string;
  tenant_name?: string;
  first_name?: string;
  last_name?: string;
  phone_number?: string;
  business_type?: string;
  app_interests?: string[];
  intent?: string;
  qualification_data?: string;
}

export async function cpGoogleOneTap(payload: GoogleOneTapPayload): Promise<RegisterResponse> {
  const response = await fetch(`${CONTROL_PLANE_URL}/api/method/control_plane.api.auth.google_one_tap`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'omit',
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));

    let message = 'Google sign-in failed';
    if (error._server_messages) {
      try {
        const msgs = JSON.parse(error._server_messages);
        let parsed = msgs[0];
        if (typeof parsed === 'string') {
          try { parsed = JSON.parse(parsed); } catch { /* use as-is */ }
        }
        message = typeof parsed === 'object' && parsed?.message
          ? String(parsed.message)
          : String(parsed);
      } catch {
        message = String(error._server_messages);
      }
    } else if (error.message) {
      message = error.message;
    }

    throw new Error(message);
  }

  const data = await response.json();
  return data.message;
}

// === LINE Auth (re-exported from controlPlaneLineAuthClient.ts) ===
export {
  type LineInitiateResponse,
  type LineCallbackParams,
  storeLineState,
  getLineState,
  getLineIntent,
  clearLineState,
  cpInitiateLineLogin,
  parseLineCallbackParams,
  validateLineState,
  type LineRegisterPayload,
  cpRegisterFromLine,
  type SendOTPResponse,
  type VerifyOTPResponse,
  cpSendOTP,
  cpVerifyOTP,
  cpCompleteReverify,
} from './controlPlaneLineAuthClient'

// === Custom Domain Types ===

export interface DomainStatus {
  domain: string | null
  status: 'none' | 'pending_dns' | 'provisioning_ssl' | 'active' | 'failed'
  storefront_url: string | null
}

export interface SetDomainResponse {
  status: string
  domain: string
  dns_instructions: {
    a_record: { type: string; name: string; value: string }
    note: string
  }
}

export interface VerifyDomainResponse {
  status: string
  method?: string
  error?: string
}

// === Custom Domain API Functions ===
// NOTE: Domain endpoints use allow_guest=True + explicit access_token parameter.
// Must use cpGuestApiCall (no Authorization header) because Frappe's validate_auth()
// rejects Bearer tokens before allow_guest=True is checked.

export async function cpGetDomainStatus(): Promise<DomainStatus> {
  const token = getCPToken()
  return cpGuestApiCall<DomainStatus>('control_plane.api.domain.get_domain_status', {
    method: 'POST',
    body: JSON.stringify({ access_token: token }),
  })
}

export async function cpSetCustomDomain(domain: string): Promise<SetDomainResponse> {
  const token = getCPToken()
  return cpGuestApiCall<SetDomainResponse>('control_plane.api.domain.set_custom_domain', {
    method: 'POST',
    body: JSON.stringify({ access_token: token, domain }),
  })
}

export async function cpVerifyDomain(): Promise<VerifyDomainResponse> {
  const token = getCPToken()
  return cpGuestApiCall<VerifyDomainResponse>('control_plane.api.domain.verify_domain', {
    method: 'POST',
    body: JSON.stringify({ access_token: token }),
  })
}

export async function cpRemoveDomain(): Promise<{ status: string; domain: null }> {
  const token = getCPToken()
  return cpGuestApiCall<{ status: string; domain: null }>('control_plane.api.domain.remove_domain', {
    method: 'POST',
    body: JSON.stringify({ access_token: token }),
  })
}

// === Domain Purchase API Types & Functions ===

export interface DomainSearchResult {
  domain: string
  available: boolean
  price_usd: number
  currency: string
  premium: boolean
}

export interface DomainSearchResponse {
  results: DomainSearchResult[]
}

export async function cpSearchDomains(query: string): Promise<DomainSearchResponse> {
  const token = getCPToken()
  return cpGuestApiCall<DomainSearchResponse>('control_plane.api.domain_purchase.search_domains', {
    method: 'POST',
    body: JSON.stringify({ access_token: token, query }),
  })
}

export interface DomainPurchaseResponse {
  purchase_id: string
  domain: string
  stage: string
}

export interface DomainPurchaseStatus {
  purchase_id: string
  domain: string
  stage: string
  stage_label: string
  is_complete: boolean
  error: string | null
}

export async function cpPurchaseDomain(domain: string): Promise<DomainPurchaseResponse> {
  const token = getCPToken()
  return cpGuestApiCall<DomainPurchaseResponse>('control_plane.api.domain_purchase.purchase_domain', {
    method: 'POST',
    body: JSON.stringify({ access_token: token, domain }),
  })
}

export async function cpGetPurchaseStatus(purchaseId: string): Promise<DomainPurchaseStatus> {
  const token = getCPToken()
  return cpGuestApiCall<DomainPurchaseStatus>('control_plane.api.domain_purchase.get_purchase_status', {
    method: 'POST',
    body: JSON.stringify({ access_token: token, purchase_id: purchaseId }),
  })
}

export async function cpRetryPurchase(purchaseId: string): Promise<DomainPurchaseResponse> {
  const token = getCPToken()
  return cpGuestApiCall<DomainPurchaseResponse>('control_plane.api.domain_purchase.retry_purchase', {
    method: 'POST',
    body: JSON.stringify({ access_token: token, purchase_id: purchaseId }),
  })
}

// === LINE OA Management (re-exported from controlPlaneLineOAClient.ts) ===
export {
  type LineConfigResponse,
  type LineConfigUpdate,
  type LineConfigUpdateResponse,
  cpGetLineConfig,
  cpUpdateLineConfig,
  type LineMessagingStatusResponse,
  type LineMessagingConnectResponse,
  type LineMessagingDisconnectResponse,
  cpGetLineMessagingStatus,
  cpConnectLineMessaging,
  cpConnectLineMessagingByCredentials,
  cpDisconnectLineMessaging,
  type LineNotificationPreferencesUpdate,
  cpUpdateLineNotificationPreferences,
  type LineOADashboardResponse,
  cpGetLineOADashboard,
  type LineNotificationActivity,
  type LineNotificationTypeActivity,
  cpGetLineNotificationActivity,
  type LineOASparklinePoint,
  type LineOASparklineSummary,
  type LineOASparklineResponse,
  cpGetLineOASparkline,
  type LineRichMenuArea,
  type LineRichMenu,
  type LineRichMenuListResponse,
  type LineRichMenuCreateResponse,
  cpGetLineOARichMenuList,
  cpCreateLineOARichMenu,
  cpUploadLineOARichMenuImage,
  cpDeleteLineOARichMenu,
  cpSetLineOARichMenuDefault,
  cpClearLineOARichMenuDefault,
  type LineBroadcastLogEntry,
  type LineBroadcastHistoryResponse,
  type LineBroadcastQuotaResponse,
  type LineBroadcastSendResponse,
  cpGetLineOABroadcastQuota,
  cpGetLineOABroadcastHistory,
  cpSendLineOABroadcast,
  type LineScheduledBroadcast,
  type LineScheduledListResponse,
  cpScheduleLineOABroadcast,
  cpGetLineOABroadcastScheduled,
  cpCancelLineOABroadcast,
  type LineFollowerProfile,
  type LineAudienceListResponse,
  cpGetLineOAAudienceList,
  type LineCampaignSegment,
  type LineCampaignRecipient,
  type LineCampaignSegmentResponse,
  type LineCampaignSendResponse,
  type LineCampaignFilterConnector,
  type LineCampaignFilterType,
  type LineCampaignFilterCondition,
  type LineCampaignFilterOptions,
  type LineCampaignHistoryEntry,
  type LineCampaignHistoryResponse,
  type LineCampaignRecipientEntry,
  type LineCampaignRecipientsResponse,
  type LineCampaignVisitsResponse,
  type CampaignMedia,
  cpGetLineCampaignSegment,
  cpGetLineCampaignFilterOptions,
  cpSendLineCampaign,
  cpGetLineCampaignHistory,
  cpGetLineCampaignRecipients,
  cpGetLineCampaignVisits,
} from './controlPlaneLineOAClient'

// === Tenant Settings API Types & Functions ===

export type WarehouseStrategy = 'all' | 'selected'

export interface TenantSettingsResponse {
  selling_price_list: string
  warehouse_strategy: WarehouseStrategy
  hide_out_of_stock: number
  auto_slip_verify_enabled: number
  warehouses: string[]
  shipping_cost_text: string
  shipping_delivery_text: string
  event_require_slip: number
  default_personalization_item: string
  auto_invoice_on_payment: number
  allow_staff_manual_verification: number
  skip_slip_verification: number
  enable_stock_reservation: number
  stock_deduction_timing: string
  line_only_login: number
  require_phone_otp: number
  show_fake_viewers: number
  fake_viewer_min: number
  fake_viewer_max: number
  show_stock_quantity: number
  enable_b2b_quotation: number
  enable_cart_sharing: number
  show_prices: number
  pdp_contact_button: number
  oos_line_chat: number
  oos_button_url: string
  oos_button_text: string
  oos_button_color: string
  // Review settings
  enable_reviews: number
  review_auto_approve: number
  review_auto_approve_min_rating: number
  review_rating_style: string
  review_reminder_enabled: number
  review_reminder_days: number
  // Loyalty
  loyalty_enabled: number
  show_loyalty_in_cart: number
  show_loyalty_in_checkout: number
  show_loyalty_in_account: number
  // Chargeable-weight shipping (read-only display; edited in Frappe Desk)
  custom_shipping_chargeable_enabled: number
  custom_shipping_flat_rate: number
  custom_shipping_rate_under: number
  custom_shipping_rate_over: number
  custom_shipping_value_threshold: number
  custom_shipping_free_weight_kg: number
  custom_shipping_volumetric_divisor: number
  custom_shipping_default_weight_uom: string
  custom_shipping_income_account: string
  // OG metadata
  og_image_url: string
  og_description: string
  // Store name (browser tab title)
  store_name: string
  // Favicon / site icon
  site_icon_url: string
  // Image protection
  disable_image_right_click: number
  // Branding
  hide_powered_by: number
  // PDPA consent
  enable_pdpa_consent: number
  pdpa_policy_url: string
  pdpa_consent_text: string
  // Ads tracking
  enable_ads_tracking: number
  meta_pixel_id: string
  meta_capi_access_token: string
  meta_test_event_code: string
  google_ads_customer_id: string
  google_ads_conversion_action_id: string
  google_ads_conversion_label: string
  ga4_measurement_id: string
  ga4_api_secret: string
  utm_cookie_days: number
  // Multi-language catalog (Plan 1928)
  storefront_language: string
  enabled_languages: string
}

export interface TenantSettingsUpdate {
  selling_price_list?: string
  warehouse_strategy?: WarehouseStrategy
  hide_out_of_stock?: number
  auto_slip_verify_enabled?: number
  event_require_slip?: number
  warehouses?: string[]
  default_personalization_item?: string
  auto_invoice_on_payment?: number
  allow_staff_manual_verification?: number
  skip_slip_verification?: number
  enable_stock_reservation?: number
  stock_deduction_timing?: string
  line_only_login?: number
  require_phone_otp?: number
  show_fake_viewers?: number
  fake_viewer_min?: number
  fake_viewer_max?: number
  show_stock_quantity?: number
  enable_b2b_quotation?: number
  enable_cart_sharing?: number
  show_prices?: number
  pdp_contact_button?: number
  oos_line_chat?: number
  oos_button_url?: string
  oos_button_text?: string
  oos_button_color?: string
  // Review settings
  enable_reviews?: number
  review_auto_approve?: number
  review_auto_approve_min_rating?: number
  review_rating_style?: string
  review_reminder_enabled?: number
  review_reminder_days?: number
  // Loyalty
  loyalty_enabled?: number
  show_loyalty_in_cart?: number
  show_loyalty_in_checkout?: number
  show_loyalty_in_account?: number
  // OG metadata
  og_image_url?: string
  og_description?: string
  // Store name (browser tab title)
  store_name?: string
  // Favicon / site icon
  site_icon_url?: string
  // Image protection
  disable_image_right_click?: number
  // Branding
  hide_powered_by?: number
  // PDPA consent
  enable_pdpa_consent?: number
  pdpa_policy_url?: string
  pdpa_consent_text?: string
  // Ads tracking
  enable_ads_tracking?: number
  meta_pixel_id?: string
  meta_capi_access_token?: string
  meta_test_event_code?: string
  google_ads_customer_id?: string
  google_ads_conversion_action_id?: string
  google_ads_conversion_label?: string
  ga4_measurement_id?: string
  ga4_api_secret?: string
  utm_cookie_days?: number
  // Multi-language catalog (Plan 1928)
  storefront_language?: string
  enabled_languages?: string
}

export async function cpGetTenantSettings(storefrontKey?: string): Promise<TenantSettingsResponse> {
  const token = getCPToken() ?? ''
  const body: Record<string, string> = { access_token: token }
  if (storefrontKey) body.storefront_key = storefrontKey
  return cpGuestApiCall<TenantSettingsResponse>(
    'control_plane.api.tenant_settings.get_settings',
    { method: 'POST', body: JSON.stringify(body) }
  )
}

export async function cpUpdateTenantSettings(
  data: TenantSettingsUpdate,
  storefrontKey?: string,
): Promise<TenantSettingsResponse> {
  const token = getCPToken()
  const body: Record<string, unknown> = { access_token: token, ...data }
  if (storefrontKey) body.storefront_key = storefrontKey
  return cpGuestApiCall<TenantSettingsResponse>(
    'control_plane.api.tenant_settings.update_settings',
    { method: 'POST', body: JSON.stringify(body) }
  )
}

// === Profile Fields API ===

export interface ProfileFieldConfig {
  fieldname: string
  label: string
  fieldtype: 'Data' | 'Email' | 'Date'
  required: number
  enabled: number
  sort_order: number
  helper_text: string
}

export async function cpGetProfileFields(): Promise<ProfileFieldConfig[]> {
  const token = getCPToken()
  return cpGuestApiCall<ProfileFieldConfig[]>(
    'control_plane.api.tenant_settings.get_profile_fields',
    { method: 'POST', body: JSON.stringify({ access_token: token }) }
  )
}

export async function cpUpdateProfileFields(
  fields: ProfileFieldConfig[]
): Promise<ProfileFieldConfig[]> {
  const token = getCPToken()
  return cpGuestApiCall<ProfileFieldConfig[]>(
    'control_plane.api.tenant_settings.update_profile_fields',
    { method: 'POST', body: JSON.stringify({ access_token: token, fields }) }
  )
}

// === Price List API ===

export interface PriceListOption {
  name: string
}

export async function cpGetAvailablePriceLists(): Promise<PriceListOption[]> {
  const token = getCPToken()
  return cpGuestApiCall<PriceListOption[]>(
    'control_plane.api.tenant_settings.get_price_lists',
    { method: 'POST', body: JSON.stringify({ access_token: token }) }
  )
}

// === Warehouse API ===

export interface WarehouseOption {
  name: string
}

export async function cpGetAvailableWarehouses(): Promise<WarehouseOption[]> {
  const token = getCPToken()
  return cpGuestApiCall<WarehouseOption[]>(
    'control_plane.api.tenant_settings.get_warehouses',
    { method: 'POST', body: JSON.stringify({ access_token: token }) }
  )
}

// === Mobile Config API ===

export type MobilePreset = 'app-style' | 'web-style' | 'minimal'

export interface MobileConfigResponse {
  preset: MobilePreset
  nav_style: string
  search_placement: string
  category_layout: string
  product_list_layout: string
  table_row_density: string
  table_image_size: string
  table_show_sku: number
  table_show_collections: number
  table_show_price: number
  table_show_add_to_cart: number
  default_view: string
  bottom_tab_items: Array<{ icon: string; label: string; route: string }> | null
}

export async function cpGetMobileConfig(): Promise<MobileConfigResponse> {
  const token = getCPToken()
  return cpGuestApiCall<MobileConfigResponse>(
    'control_plane.api.mobile_config.get_config',
    { method: 'POST', body: JSON.stringify({ access_token: token }) }
  )
}

export interface PublishMobileConfigPayload {
  preset: MobilePreset
  nav_style?: string
  search_placement?: string
  category_layout?: string
  product_list_layout?: string
  table_row_density?: string
  table_image_size?: string
  table_show_sku?: number
  table_show_collections?: number
  table_show_price?: number
  table_show_add_to_cart?: number
  default_view?: string
  bottom_tab_items?: Array<{ icon: string; label: string; route: string; visible: boolean }>
}

export async function cpPublishMobileConfig(
  payload: PublishMobileConfigPayload
): Promise<MobileConfigResponse> {
  const token = getCPToken()
  return cpGuestApiCall<MobileConfigResponse>(
    'control_plane.api.mobile_config.publish_config',
    { method: 'POST', body: JSON.stringify({ access_token: token, ...payload }) }
  )
}

export interface PreviewTokenResponse {
  token: string
  expires_at: number
}

export async function cpGetPreviewToken(): Promise<PreviewTokenResponse> {
  const token = getCPToken()
  return cpGuestApiCall<PreviewTokenResponse>(
    'control_plane.api.mobile_config.get_preview_token',
    { method: 'POST', body: JSON.stringify({ access_token: token }) }
  )
}

// === Domain Management Types & Functions ===

export interface DomainManagementInfo {
  domain: string
  source: 'purchased' | 'external'
  registered_at: string | null
  expires_at: string | null
  auto_renew: boolean | null
  price_usd: number | null
}

export interface SetAutoRenewResponse {
  success: boolean
  auto_renew: boolean
}

export async function cpGetDomainManagement(): Promise<DomainManagementInfo> {
  const token = getCPToken()
  return cpGuestApiCall<DomainManagementInfo>('control_plane.api.domain_purchase.get_domain_management', {
    method: 'POST',
    body: JSON.stringify({ access_token: token }),
  })
}

export async function cpSetAutoRenew(enabled: boolean): Promise<SetAutoRenewResponse> {
  const token = getCPToken()
  return cpGuestApiCall<SetAutoRenewResponse>('control_plane.api.domain_purchase.set_auto_renew', {
    method: 'POST',
    body: JSON.stringify({ access_token: token, enabled }),
  })
}
